import axios from 'axios';

export interface User {
  id: number;
  name: string;
  email: string;
  created_at: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000') + '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const fetchQuestions = async () => {
  try {
    const response = await api.get(`/questions`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching questions:', error);
    return [];
  }
};

export const login = async (
  email: string,
  password: string
): Promise<{ user: User; token: string }> => {
  const { data } = await api.post<{ user: User; token: string }>('/login', {
    email,
    password,
  });
  localStorage.setItem('authToken', data.token);
  api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
  return data;
};

export const setAuthToken = (token?: string | null) => {
  if (token) {
    localStorage.setItem('authToken', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('authToken');
    delete api.defaults.headers.common['Authorization'];
  }
};

export const register = async (
  payload: RegisterData
): Promise<AuthResponse> => {
  const { data } = await api.post<AuthResponse>('/register', payload);
  setAuthToken(data.token);
  return data;
};

export const getCategories = () => api.get('/categories');

export const getQuestionsByCategory = (categoryId: number) =>
  api.get(`/categories/${categoryId}/questions`);

export const fetchStats = () => api.get('/results');

export const getResults = async () => {
  try {
    const res = await api.get('/results');
    return res.data;
  } catch (error: any) {
    console.error('Error fetching stats:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch stats');
  }
};

export const submitQuizResult = (
  categoryId: number,
  score: number,
  total: number
) => {
  return api.post('/results', {
    category_id: categoryId,
    score: score,
    total: total,
  });
};

export const fetchUserById = (userId: number) => api.get(`/users/${userId}`);

export const getUser = () => api.get(`/user`);
